import { Material } from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs';
import { Zed } from '@modules/core';
import { Injectable } from '@modules/core/src/injectable/Injectable';
import { World } from 'miniplex';

import { Entity } from '../../ecs/Entity';

import { ZEDExtensionLoaderManager } from './ZEDExtensionLoaderManager';

import type { IAnimation, IGLTF, INode, IScene } from '@babylonjs/loaders/glTF/2.0';
export abstract class ZEDExtensionLoaderBase<T extends Entity> implements Injectable {
  protected readonly ZED_EXTENSION = 'ZED_EXTENSION';

  protected readonly world;
  protected readonly scene;
  protected readonly options;
  protected readonly extensionManager;

  constructor({
    [Zed.WORLD_TOKEN]: world,
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
    [ZEDExtensionLoaderManager.INJECTABLE_TOKEN]: extensionManager,
  }: {
    [Zed.WORLD_TOKEN]: World<T>;
    [BabylonjsModule.INJECTABLE_TOKEN]: BabylonjsModule;
    [ZEDExtensionLoaderManager.INJECTABLE_TOKEN]: ZEDExtensionLoaderManager;
  }) {
    this.world = world;
    this.scene = babylonjs.scene;
    this.options = babylonjs.options;
    this.extensionManager = extensionManager;
    this.extensionManager.registExtensionLoader(this);
  }

  public abstract getLoaderName(): string;
  public dispose(): void {
    this.extensionManager.unregistExtensionLoader(this);
  }

  protected abstract createEntity<T2 extends Omit<T, 'entityId'>>(
    entityId: string,
    components?: T2,
    extensionData?: any
  ): T | undefined;

  public abstract initializeMaterial(material: Material): void;
  public initializeComponents(data: IGLTF, extensionData?: any): T[] {
    let entities: T[] = [];

    const scenes = data.scenes;

    if (scenes && scenes.length > 0) {
      const scene = scenes[0];
      const extra = scene.extras;
      if (extra) {
        const entityList = this.addComponentByScene(scene, extra, extensionData);
        if (entityList) {
          entities.push(...entityList);
        }
      }
    }

    const nodes = data.nodes;
    if (nodes) {
      entities.push(
        ...nodes.flatMap((node) => {
          const entity = this.addComponentByNode(node, data.animations, extensionData);
          return entity ?? [];
        })
      );
    }
    return entities;
  }
  protected abstract addComponentByScene(scene: IScene, extra: any, extensionData?: any): T[] | undefined;
  protected abstract addComponentByNode(node: INode, animations?: IAnimation[], extensionData?: any): T[] | undefined;
}
